import httpClient from '../http/http-client';
import { IInflationIndex, IInflationIndexValue, IPostInflationIndex } from './inflation-index.model';

export default class InflationIndexService {
  http = httpClient;

  // Super-admin endpoint (full management)
  async getAll(): Promise<IInflationIndex[]> {
    return this.http.get('super-admin/inflation-index')
      .then(r => r.data.inflationIndices)
      .catch(err => { console.error(err); throw err; });
  }

  // User-facing endpoint (read-only, all active indices with values)
  async getAllActive(): Promise<IInflationIndex[]> {
    return this.http.get('inflation-index')
      .then(r => r.data.inflationIndices)
      .catch(err => { console.error(err); throw err; });
  }

  // FY-8 (super admin): which CPI years each default index is missing, measured
  // against spend factor price years and the years spend calculations are dated
  // in for the index's currency. Missing years are approximated by the closest
  // available year, so this drives a make-it-exact prompt, not a failure alarm.
  async getCoverage(): Promise<ICpiCoverage[]> {
    return this.http.get('super-admin/inflation-index/coverage')
      .then(r => r.data.coverage)
      .catch(err => { console.error(err); throw err; });
  }

  async getById(id: string): Promise<IInflationIndex> {
    return this.http.get(`super-admin/inflation-index/${id}`)
      .then(r => r.data.inflationIndex)
      .catch(err => { console.error(err); throw err; });
  }

  async create(data: IPostInflationIndex): Promise<IInflationIndex> {
    return this.http.post('super-admin/inflation-index', data)
      .then(r => r.data.inflationIndex)
      .catch(err => { console.error(err); throw err; });
  }

  async update(id: string, data: Partial<IPostInflationIndex & { isDefault: boolean; isActive: boolean }>): Promise<IInflationIndex> {
    return this.http.patch(`super-admin/inflation-index/${id}`, data)
      .then(r => r.data.inflationIndex)
      .catch(err => { console.error(err); throw err; });
  }

  async delete(id: string): Promise<any> {
    return this.http.delete(`super-admin/inflation-index/${id}`)
      .catch(err => { console.error(err); throw err; });
  }

  async getValues(id: string): Promise<IInflationIndexValue[]> {
    return this.http.get(`super-admin/inflation-index/${id}/values`)
      .then(r => r.data.values)
      .catch(err => { console.error(err); throw err; });
  }

  async upsertValues(id: string, values: IInflationIndexValue[]): Promise<IInflationIndexValue[]> {
    return this.http.post(`super-admin/inflation-index/${id}/values`, { values })
      .then(r => r.data.values)
      .catch(err => { console.error(err); throw err; });
  }

  async deleteValue(id: string, year: number): Promise<any> {
    return this.http.delete(`super-admin/inflation-index/${id}/values/${year}`)
      .catch(err => { console.error(err); throw err; });
  }

  async fetchExternalValues(id: string, source?: string, years?: number[]): Promise<IInflationIndexValue[]> {
    return this.http.post(`super-admin/inflation-index/${id}/fetch-external`, { source, years })
      .then(r => r.data.values)
      .catch(err => { console.error(err); throw err; });
  }
}

export interface ICpiCoverage {
  indexId: string;
  indexName: string;
  currencyCode: string;
  presentYears: number[];
  requiredYears: number[];
  missingYears: number[];
}
